import { Activity } from './Activity';
import { LanguageCode } from './Language';

export enum GrowthArea {
  connectionAndCommunication = 'connection-and-communication',
  collaborationAndTeamwork = 'collaboration-and-teamwork',
  managementAndLeadership = 'management-and-leadership',
  productivityAndResults = 'productivity-and-results',
  researchAndTechnology = 'research-and-technology',
  innovationAndCreation = 'innovation-and-creation',
  wellbeingAndSupport = 'wellbeing-and-support',
  securityAndControl = 'security-and-control',
}

export enum LessonMode {
  cherryPick = 'cherry-pick',
  story = 'story',
}

export enum LessonStepCardBackgroundPosition {
  start = 'start',
  end = 'end',
}

export enum LessonStepCardBackgroundShape {
  convex = 'convex',
  concave = 'concave',
  rectangle = 'rectangle',
  slope = 'slope',
}

export enum EnhancerSemanticType {
  checkIn = 'check-in',
  quickRecap = 'quick-recap',
  quickReflection = 'quick-reflection',
  checkOut = 'check-out',
}

export enum EnhancerFunctionalType {
  dropdown = 'dropdown-enhancer',
  multipleAnswer = 'multiple-answer-enhancer',
  progress = 'progress-enhancer',
  simpleText = 'simple-text-enhancer',
  slider = 'slider-enhancer',
  input = 'input-enhancer',
  storyline = 'storyline-enhancer',
}

export enum EnhancerSimpleTextVariant {
  didYouKnow = 'did-you-know',
  quote = 'quote',
}

export enum EnhancerMultipleAnswerVariant {
  singleChoice = 'single-choice',
  multipleChoice = 'multiple-choice',
}

export type EnhancerAnswer = {
  id: string;
  data: unknown;
};

export type EnhancerChoice = {
  _key: string;
  _type: string;
  title: string;
  message?: string;
  isCorrect?: boolean;
};

export type Enhancer = {
  _key: string;
  _type: EnhancerFunctionalType;
  semanticType: EnhancerSemanticType;
  title?: string;
  subtitle?: string;
  message?: string;
  options?: string[] | EnhancerChoice[];
  alwaysShow?: boolean;
  image?: string;
  variant?: EnhancerSimpleTextVariant | EnhancerMultipleAnswerVariant;
  choices?: EnhancerChoice[];
  correctAnswerMessage?: string;
  wrongAnswerMessage?: string;
  poles?: string[];
  picture?: string;
  description?: string;
};

export type CheckIn = {
  primerEnhancer: Enhancer;
  description: string;
  message: string;
};

export type CheckOut = {
  title: string;
  description: string;
  message: string;
  quiz: CheckOutQuiz;
};

export type CheckOutQuiz = {
  title: string;
  questions: CheckOutQuizQuestion[];
};

export type CheckOutQuizQuestion = {
  id: string;
  question: string;
  choices: CheckOutQuizQuestionChoice[];
  correctAnswerMessage?: string;
  wrongAnswerMessage?: string;
};

export type CheckOutQuizQuestionChoice = {
  id: string;
  title: string;
  isCorrect: boolean;
};

export type Step = {
  id: string;
  activity: Activity;
  title?: string;
  actionButtonText?: string;
  activityTypeText?: string;
  duration?: number;
  image?: string;
  primerEnhancers?: Enhancer[];
  recapEnhancers?: Enhancer[];
  learningGoal?: string;
};

export type LessonEnhancer = {
  enhancer: Enhancer[];
  startPosition: number;
  endPosition?: number;
};

export type LessonTakeawaysInsights = {
  items?: string[];
  imageUrl?: string;
};

export type LessonTakeawaysMakeItHappen = {
  message?: string;
  steps?: string[];
};

export type LessonTakeawaysTips = {
  message?: string;
  imageUrl?: string;
};

export type LessonTakeaways = {
  insights?: LessonTakeawaysInsights;
  makeItHappen?: LessonTakeawaysMakeItHappen;
  tips?: LessonTakeawaysTips;
};

export type Lesson = {
  id: number;
  code: string;
  title: string;
  subtitle: string;
  description: string;
  duration: number;
  language: LanguageCode;
  learningGoals: string[];
  steps: Step[];
  imageUrl?: string;
  enhancers?: LessonEnhancer[];
  feedbackFormUrl?: string;
  teamUpActivityUrl?: string;
  audioUrl?: string;
  takeaways?: LessonTakeaways;
  growthArea?: GrowthArea;
  scientificArticleReferenceList?: {
    _key: string;
    value: { children: { _key: string; text: string; marks: string[] }[] }[];
  }[];
  checkIn?: CheckIn;
  checkOut?: CheckOut;
  groupExercise?: unknown;
  lessonType: 'masterclass' | 'softSkill' | 'digitalSkill' | 'language' | null;
  expertName: string | null;
  expertImageUrl: string | null;
};

export enum LessonStepPlayerItemType {
  enhancer = 'enhancer',
  activity = 'activity',
}

export type LessonStepPlayerItem = {
  type: LessonStepPlayerItemType;
  enhancer?: Enhancer;
  activity?: Activity;
  isOutput?: boolean;
};

export type LessonStoryStep = {
  booster?: Enhancer;
  activity?: Step;
  isOutput?: boolean;
};
